import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", attempts: 0 });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id" });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "paid" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        if (attemptsRef.current >= 8) {
          setState({ status: "timeout" });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attemptsRef.current >= 5) {
          setState({ status: "error", message: "Unable to verify payment" });
          return;
        }
        setTimeout(poll, 2500);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-[720px] mx-auto px-6 py-24" data-testid="payment-success-page">
      {state.status === "polling" && (
        <div className="text-center" data-testid="payment-polling">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <h1 className="font-serif-display text-4xl mt-8 font-light">Confirming your payment…</h1>
          <p className="mt-4 text-muted-foreground">One moment while we verify with Stripe.</p>
        </div>
      )}
      {state.status === "paid" && (
        <div className="text-center" data-testid="payment-paid">
          <div className="w-16 h-16 border-2 border-primary mx-auto flex items-center justify-center" style={{ borderRadius: 2 }}>
            <Check className="w-8 h-8 text-primary" />
          </div>
          <div className="font-mono-label text-primary mt-8">Porcelain Pro · Unlocked</div>
          <h1 className="font-serif-display text-5xl md:text-6xl mt-4 font-light leading-none tracking-tighter">Welcome to the craft.</h1>
          <p className="mt-6 text-muted-foreground max-w-lg mx-auto">
            Your account now has lifetime access to every premium masterclass. Thank you for supporting Porcelain.
          </p>
          <div className="mt-10 flex gap-3 justify-center">
            <Link to="/courses" className="btn-oxblood text-sm inline-flex items-center gap-2" data-testid="payment-continue">
              Enter Pro curriculum <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/dashboard" className="btn-outline-mono text-sm">Go to dashboard</Link>
          </div>
        </div>
      )}
      {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
        <div className="text-center" data-testid="payment-failed">
          <XCircle className="w-12 h-12 mx-auto text-destructive" />
          <h1 className="font-serif-display text-4xl mt-6 font-light">We could not confirm payment.</h1>
          <p className="mt-4 text-muted-foreground">{state.message || "The checkout session expired or is pending."}</p>
          <Link to="/pricing" className="btn-oxblood text-sm mt-8 inline-block">Try again</Link>
        </div>
      )}
    </div>
  );
}
